import React from "react";

/**
 * Chat text formatting stored in the message `text` field.
 *
 * **bold**
 * *italic*
 * ***bold italic***
 * A line starting with "- " is a bullet.
 * A line starting with "1. " (any number) is a numbered item.
 * A single newline stays a line break.
 * Markers do not apply inside @mentions.
 * Headings, code, quotes, images, HTML, and underline are left as typed.
 *
 * Keep the strip rules in sync with:
 * salon-symphony-backend/app/Utils/chatFormatting.ts
 * salon-symphony-backend/gcp/chatFormatting.js
 */

function normalizeNewlines(text) {
  return String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

function findMentionRanges(text, mentions) {
  const ranges = [];
  if (!text || !Array.isArray(mentions) || mentions.length === 0) return ranges;

  const mentionsMap = new Map();
  mentions.forEach((mention) => {
    if (
      mention &&
      mention.text &&
      mention.userId !== undefined &&
      mention.userId !== null
    ) {
      mentionsMap.set(mention.userId, mention.text);
    }
  });

  let current = 0;
  while (current < text.length) {
    let nextIndex = -1;
    let nextUserId = null;
    let nextText = null;
    mentionsMap.forEach((mentionText, userId) => {
      const index = text.indexOf(mentionText, current);
      if (index !== -1 && (nextIndex === -1 || index < nextIndex)) {
        nextIndex = index;
        nextUserId = userId;
        nextText = mentionText;
      }
    });
    if (nextIndex === -1 || nextText == null) break;
    ranges.push({
      start: nextIndex,
      end: nextIndex + nextText.length,
      userId: nextUserId,
      text: nextText,
    });
    current = nextIndex + nextText.length;
  }
  return ranges;
}

function isInMention(index, mentions) {
  return mentions.some(
    (mention) => index >= mention.start && index < mention.end,
  );
}

function readUrl(text, index, end, mentions) {
  if (text[index] !== "h") return null;
  const match = text.slice(index, end).match(/^https?:\/\/[^\s]+/);
  if (!match) return null;
  let href = match[0];
  const blocking = mentions.find(
    (mention) => mention.start > index && mention.start < index + href.length,
  );
  if (blocking) href = text.slice(index, blocking.start);
  if (!/^https?:\/\/\S+$/.test(href)) return null;
  return href;
}

function hasOpenItalic(text, from, to, mentions) {
  let open = false;
  for (let i = from; i < to; i++) {
    if (text[i] === "\n") return false;
    if (isInMention(i, mentions)) continue;
    if (text[i] !== "*") continue;
    if (text[i + 1] === "*") {
      i += text[i + 2] === "*" ? 2 : 1;
      continue;
    }
    const before = i > 0 ? text[i - 1] : "";
    const after = text[i + 1];
    const canOpen = after != null && !/\s/.test(after);
    const canClose = Boolean(before) && !/\s/.test(before);
    if (open && canClose) open = false;
    else if (!open && canOpen) open = true;
  }
  return open;
}

function findClosing(text, from, end, marker, mentions) {
  const after = text[from];
  if (after == null || /\s/.test(after)) return -1;

  for (let i = from; i < end; i++) {
    if (text[i] === "\n") return -1;
    if (isInMention(i, mentions)) continue;
    if (!text.startsWith(marker, i)) continue;
    if (marker === "*" && i + 1 < end && text[i + 1] === "*") continue;
    const before = text[i - 1];
    if (!(before && !/\s/.test(before))) continue;
    // **bo*ld*** — the first two stars of the trailing *** close the italic.
    // Skip them so bold closes on the final **.
    if (
      marker === "**" &&
      text[i + 2] === "*" &&
      hasOpenItalic(text, from, i, mentions)
    ) {
      continue;
    }
    return i;
  }
  return -1;
}

function parseInline(text, start, end, mentions) {
  const nodes = [];
  let i = start;

  const pushText = (value) => {
    if (!value) return;
    const last = nodes[nodes.length - 1];
    if (last && last.type === "text") last.text += value;
    else nodes.push({ type: "text", text: value });
  };

  while (i < end) {
    const mention = mentions.find(
      (item) => item.start === i && item.end <= end,
    );
    if (mention) {
      nodes.push({
        type: "mention",
        userId: mention.userId,
        text: mention.text,
      });
      i = mention.end;
      continue;
    }

    const href = readUrl(text, i, end, mentions);
    if (href) {
      nodes.push({ type: "link", href, text: href });
      i += href.length;
      continue;
    }

    const marker = ["***", "**", "*"].find(
      (candidate) =>
        text.startsWith(candidate, i) &&
        !(candidate === "*" && text[i + 1] === "*") &&
        !isInMention(i, mentions),
    );
    if (marker) {
      const close = findClosing(text, i + marker.length, end, marker, mentions);
      if (close !== -1) {
        const inner = parseInline(text, i + marker.length, close, mentions);
        if (inner.length) {
          if (marker === "***") {
            nodes.push({
              type: "bold",
              children: [{ type: "italic", children: inner }],
            });
          } else {
            nodes.push({
              type: marker === "**" ? "bold" : "italic",
              children: inner,
            });
          }
          i = close + marker.length;
          continue;
        }
      }
    }

    pushText(text[i]);
    i += 1;
  }

  return nodes;
}

function lineStartsInMention(line, mentions) {
  return mentions.some(
    (mention) => line.start > mention.start && line.start < mention.end,
  );
}

function splitLines(text, mentions) {
  const lines = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "\n" && !isInMention(i, mentions)) {
      lines.push({ start, end: i, text: text.slice(start, i) });
      start = i + 1;
    }
  }
  lines.push({ start, end: text.length, text: text.slice(start) });
  return lines;
}

function parseBlocks(text, mentions) {
  const lines = splitLines(text, mentions);
  const blocks = [];
  let buffer = [];

  const flush = () => {
    if (!buffer.length) return;
    const children = [];
    if (buffer.every((line) => line.text === "")) {
      buffer.forEach(() => children.push({ type: "linebreak" }));
    } else {
      buffer.forEach((line, index) => {
        if (index > 0) children.push({ type: "linebreak" });
        children.push(...parseInline(text, line.start, line.end, mentions));
      });
    }
    blocks.push({ type: "lines", children });
    buffer = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!lineStartsInMention(line, mentions) && line.text.startsWith("- ")) {
      flush();
      const items = [];
      while (i < lines.length) {
        const current = lines[i];
        if (
          lineStartsInMention(current, mentions) ||
          !current.text.startsWith("- ")
        ) {
          break;
        }
        items.push({
          children: parseInline(text, current.start + 2, current.end, mentions),
        });
        i += 1;
      }
      blocks.push({ type: "bullet-list", items });
      i -= 1;
      continue;
    }

    const numberMatch =
      !lineStartsInMention(line, mentions) && line.text.match(/^(\d+)\. /);
    if (numberMatch) {
      flush();
      const items = [];
      while (i < lines.length) {
        const current = lines[i];
        const match =
          !lineStartsInMention(current, mentions) &&
          current.text.match(/^(\d+)\. /);
        if (!match) break;
        items.push({
          value: Number(match[1]),
          children: parseInline(
            text,
            current.start + match[1].length + 2,
            current.end,
            mentions,
          ),
        });
        i += 1;
      }
      blocks.push({ type: "numbered-list", items });
      i -= 1;
      continue;
    }

    buffer.push(line);
  }

  flush();
  return blocks;
}

export function parseChatMessage(raw, mentions) {
  const text = normalizeNewlines(raw || "");
  return parseBlocks(text, findMentionRanges(text, mentions));
}

/**
 * One block per visual line or list, for the composer surface.
 * Line children are the same inline nodes the bubble renderer uses.
 */
export function markdownToComposerBlocks(raw, mentions) {
  const text = normalizeNewlines(raw || "");
  const ranges = findMentionRanges(text, mentions || []);
  const lines = splitLines(text, ranges);
  const blocks = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!lineStartsInMention(line, ranges) && line.text.startsWith("- ")) {
      const items = [];
      while (i < lines.length) {
        const current = lines[i];
        if (
          lineStartsInMention(current, ranges) ||
          !current.text.startsWith("- ")
        ) {
          break;
        }
        items.push({
          children: parseInline(text, current.start + 2, current.end, ranges),
        });
        i += 1;
      }
      blocks.push({ type: "bullet", items });
      i -= 1;
      continue;
    }

    const numberMatch =
      !lineStartsInMention(line, ranges) && line.text.match(/^(\d+)\. /);
    if (numberMatch) {
      const items = [];
      while (i < lines.length) {
        const current = lines[i];
        const match =
          !lineStartsInMention(current, ranges) &&
          current.text.match(/^(\d+)\. /);
        if (!match) break;
        items.push({
          value: Number(match[1]),
          children: parseInline(
            text,
            current.start + match[1].length + 2,
            current.end,
            ranges,
          ),
        });
        i += 1;
      }
      blocks.push({ type: "numbered", items });
      i -= 1;
      continue;
    }

    blocks.push({
      type: "line",
      children: parseInline(text, line.start, line.end, ranges),
    });
  }

  return blocks;
}

function flatten(blocks) {
  let out = "";
  const walk = (node) => {
    if (!node) return;
    if (
      node.type === "text" ||
      node.type === "mention" ||
      node.type === "link"
    ) {
      out += node.text || "";
      return;
    }
    if (node.type === "linebreak") {
      out += "\n";
      return;
    }
    if (
      node.type === "bold" ||
      node.type === "italic" ||
      node.type === "lines"
    ) {
      (node.children || []).forEach(walk);
      return;
    }
    if (node.type === "bullet-list" || node.type === "numbered-list") {
      (node.items || []).forEach((item) => {
        if (out && !out.endsWith("\n")) out += "\n";
        (item.children || []).forEach(walk);
        if (!out.endsWith("\n")) out += "\n";
      });
    }
  };
  blocks.forEach(walk);
  return out
    .replace(/[ \t]*\n[ \t]*/g, " ")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

export function stripChatFormatting(text) {
  if (typeof text !== "string" || text.length === 0) return "";
  return flatten(parseChatMessage(text, []));
}

export function plainChatText(text) {
  if (typeof text !== "string") return "";
  const plain = stripChatFormatting(text);
  return plain || text;
}

function lineBounds(text, index) {
  const safe = Math.max(0, Math.min(index, text.length));
  const start = text.lastIndexOf("\n", safe - 1) + 1;
  let end = text.indexOf("\n", safe);
  if (end === -1) end = text.length;
  return { start, end };
}

function listPrefix(line) {
  if (line.startsWith("- ")) return { type: "bullet", rest: line.slice(2) };
  const match = line.match(/^(\d+)\. (.*)$/);
  if (match) return { type: "numbered", rest: match[2] };
  return { type: null, rest: line };
}

function mapCaret(offset, blockStart, lines, parsed, nextLines) {
  const rel = offset - blockStart;
  const oldOffsets = [];
  let acc = 0;
  lines.forEach((line) => {
    oldOffsets.push(acc);
    acc += line.length + 1;
  });
  let lineIndex = 0;
  for (let i = 0; i < oldOffsets.length; i++) {
    if (rel >= oldOffsets[i]) lineIndex = i;
  }
  const caretInLine = rel - oldOffsets[lineIndex];
  const oldPrefix = lines[lineIndex].length - parsed[lineIndex].rest.length;
  const inPrefix = caretInLine < oldPrefix;
  const contentOffset = inPrefix
    ? 0
    : Math.min(caretInLine - oldPrefix, parsed[lineIndex].rest.length);
  const newOffsets = [];
  acc = 0;
  nextLines.forEach((line) => {
    newOffsets.push(acc);
    acc += line.length + 1;
  });
  const newPrefix = nextLines[lineIndex].length - parsed[lineIndex].rest.length;
  return blockStart + newOffsets[lineIndex] + newPrefix + contentOffset;
}

function applyListFormat(text, start, end, format) {
  const from = lineBounds(text, start).start;
  let toIndex = end;
  if (end > start && text[end - 1] === "\n") toIndex = end - 1;
  const to = lineBounds(text, toIndex).end;
  const lines = text.slice(from, to).split("\n");
  const parsed = lines.map(listPrefix);
  const remove = parsed.every((line) => line.type === format);
  const nextLines = parsed.map((line, index) => {
    if (remove) return line.rest;
    if (format === "bullet") return `- ${line.rest}`;
    return `${index + 1}. ${line.rest}`;
  });
  const next = text.slice(0, from) + nextLines.join("\n") + text.slice(to);
  const clamp = (offset) => Math.min(Math.max(offset, from), to);
  if (start === end) {
    const caret = mapCaret(clamp(start), from, lines, parsed, nextLines);
    return { value: next, selectionStart: caret, selectionEnd: caret };
  }
  return {
    value: next,
    selectionStart: mapCaret(clamp(start), from, lines, parsed, nextLines),
    selectionEnd: mapCaret(clamp(end), from, lines, parsed, nextLines),
  };
}

function isWrapped(text, start, end, marker) {
  if (start < marker.length || end + marker.length > text.length) return false;
  if (text.slice(start - marker.length, start) !== marker) return false;
  if (text.slice(end, end + marker.length) !== marker) return false;
  if (marker === "*") {
    if (text[start - 2] === "*" || text[end + 1] === "*") return false;
  }
  if (marker === "**") {
    if (text[start - 3] === "*" || text[end + 2] === "*") return false;
  }
  return true;
}

function selectionIncludesMarker(selected, marker) {
  if (selected.length < marker.length * 2 + 1) return false;
  if (!selected.startsWith(marker) || !selected.endsWith(marker)) return false;
  if (marker === "*" && selected.startsWith("**")) return false;
  if (marker === "**" && selected.startsWith("***")) return false;
  return true;
}

function applyInlineFormat(text, start, end, marker) {
  const selected = text.slice(start, end);
  if (isWrapped(text, start, end, marker)) {
    const value =
      text.slice(0, start - marker.length) +
      selected +
      text.slice(end + marker.length);
    const selectionStart = start - marker.length;
    return {
      value,
      selectionStart,
      selectionEnd: selectionStart + selected.length,
    };
  }
  if (selectionIncludesMarker(selected, marker)) {
    const inner = selected.slice(
      marker.length,
      selected.length - marker.length,
    );
    return {
      value: text.slice(0, start) + inner + text.slice(end),
      selectionStart: start,
      selectionEnd: start + inner.length,
    };
  }
  const value =
    text.slice(0, start) + marker + selected + marker + text.slice(end);
  if (start === end) {
    const caret = start + marker.length;
    return { value, selectionStart: caret, selectionEnd: caret };
  }
  return {
    value,
    selectionStart: start + marker.length,
    selectionEnd: end + marker.length,
  };
}

export function applyChatFormat(value, selectionStart, selectionEnd, format) {
  const text = value || "";
  const start = Math.max(
    0,
    Math.min(selectionStart ?? text.length, text.length),
  );
  const end = Math.max(start, Math.min(selectionEnd ?? start, text.length));
  if (format === "bold") return applyInlineFormat(text, start, end, "**");
  if (format === "italic") return applyInlineFormat(text, start, end, "*");
  if (format === "bullet" || format === "numbered") {
    return applyListFormat(text, start, end, format);
  }
  return { value: text, selectionStart: start, selectionEnd: end };
}

function renderNode(node, onMentionClick, keys) {
  const key = `chat-fmt-${keys.n}`;
  keys.n += 1;

  if (node.type === "text")
    return <React.Fragment key={key}>{node.text}</React.Fragment>;
  if (node.type === "linebreak") return <br key={key} />;
  if (node.type === "bold") {
    return (
      <strong key={key}>
        {node.children.map((child) => renderNode(child, onMentionClick, keys))}
      </strong>
    );
  }
  if (node.type === "italic") {
    return (
      <em key={key}>
        {node.children.map((child) => renderNode(child, onMentionClick, keys))}
      </em>
    );
  }
  if (node.type === "link") {
    return (
      <a key={key} href={node.href} target="_blank" rel="noopener noreferrer">
        {node.text}
      </a>
    );
  }
  if (node.type === "mention") {
    const isGroup = String(node.userId) === "0";
    return (
      <span
        key={key}
        className={`mention${isGroup ? " group" : ""}`}
        onClick={
          isGroup
            ? undefined
            : (event) => {
                event.preventDefault();
                event.stopPropagation();
                if (onMentionClick) onMentionClick(node.userId);
              }
        }
      >
        {node.text}
      </span>
    );
  }
  if (node.type === "lines") {
    return (
      <React.Fragment key={key}>
        {node.children.map((child) => renderNode(child, onMentionClick, keys))}
      </React.Fragment>
    );
  }
  if (node.type === "bullet-list") {
    return (
      <ul key={key}>
        {node.items.map((item) => {
          const itemKey = `chat-fmt-${keys.n}`;
          keys.n += 1;
          return (
            <li key={itemKey}>
              {item.children.map((child) =>
                renderNode(child, onMentionClick, keys),
              )}
            </li>
          );
        })}
      </ul>
    );
  }
  if (node.type === "numbered-list") {
    return (
      <ol key={key}>
        {node.items.map((item) => {
          const itemKey = `chat-fmt-${keys.n}`;
          keys.n += 1;
          return (
            <li key={itemKey} value={item.value}>
              {item.children.map((child) =>
                renderNode(child, onMentionClick, keys),
              )}
            </li>
          );
        })}
      </ol>
    );
  }
  return null;
}

export function renderChatMessage(text, mentions, onMentionClick) {
  if (!text) return null;
  const blocks = parseChatMessage(text, mentions);
  const keys = { n: 0 };
  return blocks.map((block) => renderNode(block, onMentionClick, keys));
}
